import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { Text } from '@chakra-ui/react'
import { colors } from '@hb/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormState } from 'react-final-form'
import { ActionButton } from '../Buttons'
import { useDocumentForm } from '../RichText/context'

export const GoToNextField = ({ inputIds }: { inputIds: string[] }) => {
  const { focusInput, goNextById } = useDocumentForm()
  const { active } = useFormState({ subscription: { active: true } })
  const [prevActive, setPrevActive] = useState<string | null>(null)
  useEffect(() => {
    if (active) setPrevActive(active)
  }, [active])

  const { nextInput, atEnd } = useMemo(() => {
    if (!prevActive) return { nextInput: inputIds[0] ?? null, atEnd: false }
    const activeInputIdx = inputIds.findIndex(input => input === prevActive)
    const nextInput = inputIds[activeInputIdx + 1] ?? null
    return {
      nextInput: nextInput ?? inputIds[0] ?? null,
      atEnd: inputIds.length > 1 && activeInputIdx === inputIds.length - 1,
    }
  }, [prevActive, inputIds])

  const handleGoToNext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      // if curr input has toNext, call it
      const wentNext = prevActive ? goNextById(prevActive) : false
      if (wentNext) return
      if (nextInput) {
        focusInput(nextInput)
      } else {
        focusInput(inputIds[0])
      }
    },
    [nextInput, focusInput, inputIds, goNextById, prevActive],
  )

  return (
    <ActionButton
      position="absolute"
      top="4px"
      right="12px"
      paddingRight={1}
      paddingLeft={3}
      paddingY={1}
      zIndex={5}
      size="xs"
      opacity={nextInput ? 1 : 0}
      boxShadow="1px 1px 3px rgba(0,0,0,0.3)"
      pointerEvents={nextInput ? 'auto' : 'none'}
      transition="opacity 300ms"
      onClick={handleGoToNext}>
      <Text>{atEnd ? 'Back to Start' : 'Next Field'}</Text>
      {atEnd ? (
        <ChevronUpIcon w={6} h={6} color={colors.green.hex} />
      ) : (
        <ChevronDownIcon w={6} h={6} color={colors.green.hex} />
      )}
    </ActionButton>
  )
}

export const GoToNextFieldRichText = () => {
  const { field: rootField, document, toBeSignedByMe } = useDocumentForm()

  const inputIds = useMemo(() => {
    if (!rootField || !document || !toBeSignedByMe) return []
    return Object.entries(rootField.children).map(([id]) => id)
  }, [rootField, document, toBeSignedByMe])
  return <GoToNextField inputIds={inputIds} />
}
